import { useState, useEffect, type MouseEvent } from 'react';
import jsCookie from 'js-cookie';
import { useLocale, useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { FireIcon } from '@phosphor-icons/react';
import { Button, IconButton, Stack, Tooltip, Typography } from '@unity/cloud-ui';
import { keyframes, styled } from '@unity/cloud-ui/styles';
import { usePathname } from '@/i18n/routing';
import { getClientEnv } from '@/lib/constants/environment';
import { getLocation } from '@/utils/get-location';
import { NavigationLink } from '@/components/navigation-link';

const { PREVIEW } = getClientEnv();

const GITHUB_BASE_URL = 'https://github.com/Unity-Technologies/docs-content/pull';

const previewIconKeyframe = keyframes`
    0% { color: #DC143C; }
    25% { color: #CE4320; }
    50% { color: #FFD700; }
    75% { color: #CE4320; }
    100% { color: #DC143C; }
`;

const StyledPreviewIcon = styled(FireIcon)(() => ({
  animation: `${previewIconKeyframe} 3.5s infinite linear`,
}));

const createLocalizedPreviewUrl = (locale: string, pathname: string, params: URLSearchParams) => {
  return `/${locale.toLowerCase()}${pathname}?${params.toString()}`;
};

export const Preview = () => {
  const t = useTranslations();
  const pathname = usePathname();
  const locale = useLocale();
  const searchParams = useSearchParams();
  const [previewVersion, setPreviewVersion] = useState(null);

  useEffect(() => {
    const cookiePreviewVersion = jsCookie.get(PREVIEW);

    if (cookiePreviewVersion) {
      setPreviewVersion(cookiePreviewVersion);
    }
  }, []);

  // Ensure the preview version is always appended to the URL
  useEffect(() => {
    if (!previewVersion) return;

    const currentPreviewParam = searchParams.get(PREVIEW);
    if (currentPreviewParam) return;

    // Update preview param if not already set
    const params = new URLSearchParams(searchParams.toString());
    params.set(PREVIEW, previewVersion);

    // Preserve the current hash
    const currentHash = getLocation().hash;

    // Update URL without causing a page reload
    window.history.replaceState(null, '', `${createLocalizedPreviewUrl(locale, pathname, params)}${currentHash}`);
  }, [searchParams, locale, pathname, previewVersion]);

  const handleOnLiveClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const params = new URLSearchParams(searchParams.toString());

    params.delete(PREVIEW);
    jsCookie.remove(PREVIEW);

    getLocation().assign(createLocalizedPreviewUrl(locale, pathname, params));
  };

  if (!previewVersion) {
    return null;
  }

  const pullRequestLink = `${GITHUB_BASE_URL}/${previewVersion}`;

  return (
    <Tooltip
      title={
        <Stack alignItems="center">
          <Typography fontWeight="bold">{t('header:preview-version:current-version-label')}</Typography>
          <NavigationLink href={pullRequestLink} external>
            {previewVersion}
          </NavigationLink>

          <Button variant="contained" onClick={handleOnLiveClick} sx={{ marginTop: 1 }}>
            {t('header:preview-version:use-live-version')}
          </Button>
        </Stack>
      }
      slotProps={{
        tooltip: {
          sx: {
            padding: 2,
          },
        },
      }}
    >
      <IconButton aria-label={t('header:preview-version:aria-label')}>
        <StyledPreviewIcon />
      </IconButton>
    </Tooltip>
  );
};
